import { Box, Card, CardContent, Divider, Typography } from "@mui/material";
import React from "react";
import AddIcon from "@mui/icons-material/Add";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import StorefrontIcon from "@mui/icons-material/Storefront";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { useParams } from "react-router-dom";
import SearchHeaderContainer from "../components/table/SearchHeaderContainer";
import { COLORS } from "../assets/theme";
import { 
  fetchStock, 
  fetchStockSummary, 
  createStock, 
  transferStock, 
  adjustStock,
  deleteStock
} from "../services/StocksNewService";
import CustomLabel from "../components/CustomLabel";
import CustomTable from "../components/table/CustomTable";
import { getAllBusinesses } from "../services/VenueService";
import GenericFormDialog, { Field } from "../components/GenericFormDialog";
import { fetchProducts } from "../services/ProductsNewService";
import { ProductRow } from "./Products";
import PurchaseItemsEditor from "../components/purchases/PurchaseItemsEditor";
import { toast } from "react-toastify";
import { createExpense } from "../services/newExpensesServices";
import { listAccounts } from "../services/newAccountsServices";
import { fetchSuppliers } from "../services/SuppliersService";
import CustomSelect from "../components/CustomSelect";
import CustomTextField from "../components/CustomTextField";
import Actions from "../components/Actions";
import ConfirmDialog from "../components/ConfirmDialog";


type StockRow = {
  id: string;
  productId: string;
  businessId: string;
  productName: string;
  productType: string;
  qty: number;
  costPrice: number | null;
  salePrice: number | null;
  totalCost: number | null;
  totalRetail: number | null;
  profit: number | null;
  businessName: string;
  updatedAt: string | Date;
};
const Stocks: React.FC = () => {
  const businessId = useParams().businessId;
  const [search, setSearch] = React.useState("");
  const [stockSummary, setStockSummary] = React.useState<any>(null);
  const [loading, setLoading] = React.useState(false);
  const [summaryError, setSummaryError] = React.useState<string | null>(null);
  const [rows,setRows] = React.useState<StockRow[]>([]);
  const [tableLoading, setTableLoading] = React.useState(false);
  const [openTransferDialog,setOpenTransferDialog] = React.useState(false);
  const [openAddStockDialog, setOpenAddStockDialog] = React.useState(false);
  const [stockDialogTab, setStockDialogTab] = React.useState(0);
  const [businessList,setBusinessList] = React.useState<any[]>([]);
  const [productsList,setProductsList] = React.useState<any[]>([]);
  const [accounts, setAccounts] = React.useState<any[]>([]);
  const [loadingAccounts, setLoadingAccounts] = React.useState(false);
  const [suppliers, setSuppliers] = React.useState<any[]>([]);
  const [loadingSuppliers, setLoadingSuppliers] = React.useState(false);
  const [adjustOpen,setAdjustOpen] = React.useState(false)
  const [adjustInitialValues, setAdjustInitialValues] = React.useState<Record<string, any>>({
    businessId: businessId || "",
    productId: "",
    qty: "",
  });
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [toDeleteRow, setToDeleteRow] = React.useState<StockRow | null>(null);

  const currencyFormatter = React.useMemo(
    () =>
      new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }),
    []
  );

  React.useEffect(() => {
    let mounted = true;
    const loadStockSummary = async () => {
      setLoading(true);
      setSummaryError(null);
      try {
        const summary = await fetchStockSummary(businessId);
        if (mounted) setStockSummary(summary);
      } catch (error: any) {
        if (mounted) {
          setSummaryError(
            error?.response?.data?.message ||
              error?.response?.data?.error ||
              error?.message ||
              "Failed to load summary."
          );
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadStockSummary();
    return () => {
      mounted = false;
    };
  }, [businessId]);

  const pickStockList = (payload: any): any[] => {
    if (!payload) return [];
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload.data)) return payload.data;
    if (Array.isArray(payload.stock)) return payload.stock;
    if (Array.isArray(payload.stocks)) return payload.stocks;
    return [];
  };

  const mapStockRecord = (item: any): StockRow => {
    const qty = Number(item?.qty ?? 0);
    const costPrice =
      item?.product?.cost_price ?? item?.product?.costPrice ?? null;
    const salePrice =
      item?.product?.sale_price ?? item?.product?.salePrice ?? null;
    const totalCost =
      costPrice !== null && costPrice !== undefined ? qty * costPrice : null;
    const totalRetail =
      salePrice !== null && salePrice !== undefined ? qty * salePrice : null;
    const profit =
      totalCost !== null && totalRetail !== null ? totalRetail - totalCost : null;

    return {
      id: String(item?.id || item?.product_id),
      productId: String(
        item?.product_id ??
          item?.product?.id ??
          item?.productId ??
          item?.id ??
          ""
      ),
      businessId: String(
        item?.business?.id ?? item?.business_id ?? item?.businessId ?? ""
      ),
      productName: String(item?.product?.name || ""),
      productType: String(
        item?.product?.package_type ??
          item?.product?.packageType ??
          item?.product?.type ??
          ""
      ),
      qty,
      costPrice,
      salePrice,
      totalCost,
      totalRetail,
      profit,
      businessName: String(item?.business?.name || "Main"),
      updatedAt: item?.updated_at || "",
    };
  };

  React.useEffect(() => {
    let mounted = true;
    const loadStock = async () => {
      setTableLoading(true);
      try {
        const res = await fetchStock(businessId);
        const list = pickStockList(res);
        if (mounted) setRows(list.map(mapStockRecord));
      } catch (error) {
        console.error("Failed to load stock list:", error);
      } finally {
        if (mounted) setTableLoading(false);
      }
    };

    loadStock();
    return () => {
      mounted = false;
    };
  }, [businessId]);

  const totals = stockSummary?.totals ?? {
    totalCost: 0,
    totalRetail: 0,
    totalProfit: 0,
  };

  const summaryScopeLabel =
    stockSummary?.scope === "BUSINESS"
      ? "Business Summary"
      : stockSummary?.scope === "ORGANIZATION"
      ? "Organization Summary"
      : "";

  const summaryCardSx = {
    borderRadius: 4,
    boxShadow: 2,
    minHeight: { xs: 120, sm: 140 },
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    bgcolor: COLORS.MainColor,
    border: "2px solid rgba(255, 255, 255, 0.2)",
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: 4,
    },
  };

  const summaryCardContentSx = {
    textAlign: "center",
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 0.5,
  };
  const summaryCardPrimaryTextColor = COLORS.WhiteText;
  const summaryCardSecondaryTextColor = "rgba(255, 255, 255, 0.75)";

  const fetchStockFunc = React.useCallback(async (params?: any) => {
    let content = [...rows];
    const normalizedQuery = String(params?.search || "").toLowerCase().trim();

    if (normalizedQuery) {
      content = content.filter((row) => {
        const updatedAt = row.updatedAt ? new Date(row.updatedAt) : null;
        const formattedDate =
          updatedAt && !Number.isNaN(updatedAt.getTime())
            ? `${updatedAt.getDate()}-${updatedAt.getMonth() + 1}-${updatedAt.getFullYear()}`
            : "";

        const haystack = [
          row.productName,
          row.productType,
          row.qty,
          row.costPrice,
          row.salePrice,
          row.totalCost,
          row.totalRetail,
          row.profit,
          row.businessName,
          row.productId,
          row.businessId,
          row.id,
          formattedDate,
        ]
          .filter((value) => value !== undefined && value !== null && value !== "")
          .map((value) => String(value).toLowerCase())
          .join(" ");

        return haystack.includes(normalizedQuery);
      });
    }

    const pageSize = Number(params?.size) || 10;

    return {
      content,
      number: 0,
      size: pageSize,
      totalElements: content.length,
      totalPages: Math.max(1, Math.ceil(content.length / pageSize)),
    };
  }, [rows]);

  const openEditForId = (id: string | number) => {
    const found = rows.find((r: any) => String(r.id) === String(id));
    if (found) {
      setAdjustInitialValues({
        businessId: found.businessId || businessId || "",
        productId: found.productId || found.id || "",
        qty: found.qty ?? "",
      });
      setAdjustOpen(true);
    }
  };
  const wiredActions = Actions({
    handleOpenEditModal: { handler: openEditForId },
    onDelete: {
      handler: async (id: number | string) => {
        const found = rows.find((r: any) => String(r.id) === String(id));
        if (found) {
          setToDeleteRow(found);
          setConfirmOpen(true);
        }
      },
    },
  });

  const defaultExpenseAccountId = React.useMemo(() => {
    const first = (accounts || [])[0];
    return first ? first.id : "";
  }, [accounts]);

  const summaryCards = [
    {
      label: "Total Cost",
      value: totals.totalCost,
      icon: Inventory2Icon,
    },
    {
      label: "Total Retail",
      value: totals.totalRetail,
      icon: StorefrontIcon,
    },
    {
      label: "Total Profit",
      value: totals.totalProfit,
      icon: TrendingUpIcon,
    },
  ];

  const formatValue = (value: number) =>
    currencyFormatter.format(Number(value) || 0);

  React.useEffect(() => {
    let mounted = true;
    if(!openTransferDialog && !openAddStockDialog && !adjustOpen){
      return;
    }
    const fetchBusinesses = async () => {
        try{
            const businesses = await getAllBusinesses();
            const products = await fetchProducts() as ProductRow[];
            setBusinessList(businesses);
            setProductsList(products);
        }catch (error){
            console.error("Failed to fetch businesses for transfer:", error);
        }
    }

    fetchBusinesses();
    return () => {
      mounted = false;
    };
  }, [openTransferDialog, openAddStockDialog, adjustOpen]);

  React.useEffect(() => {
    if (!openAddStockDialog) return;
    let mounted = true;
    const loadAccounts = async () => {
      setLoadingAccounts(true);
      try {
        const accountsData = await listAccounts();
        const expenseAccounts = (accountsData || []).filter((a: any) => {
          const code = String(a?.code || "").toUpperCase();
          return code === "EXPENSE";
        });
        if (mounted) setAccounts(expenseAccounts);
      } catch (error) {
        console.error("Failed to fetch accounts:", error);
        if (mounted) setAccounts([]);
      } finally {
        if (mounted) setLoadingAccounts(false);
      }
    };
    const loadSuppliers = async () => {
      setLoadingSuppliers(true);
      try {
        const res = await fetchSuppliers();
        const data = (res as any)?.data ?? res;
        const list = Array.isArray(data) ? data : data?.suppliers || [];
        if (mounted) setSuppliers(Array.isArray(list) ? list : []);
      } catch (error) {
        console.error("Failed to fetch suppliers:", error);
        if (mounted) setSuppliers([]);
      } finally {
        if (mounted) setLoadingSuppliers(false);
      }
    };
    loadAccounts();
    loadSuppliers();
    return () => {
      mounted = false;
    };
  }, [openAddStockDialog]);

  const createStockFields = React.useMemo((): Field[] => [
    {
      name: "businessId",
      label: "Business",
      type: "dropdown",
      options: businessList.map((b) => ({ label: b.name, value: b.id })),
    },
    {
      name: "productId",
      label: "Product",
      type: "dropdown",
      required: true,
      options: productsList.map((p) => ({ label: p.name, value: p.id })),
    },
    { name: "qty", label: "Quantity", type: "number", required: true },
  ], [businessList, productsList]);

  const supplierOptions = React.useMemo(() => {
    return (Array.isArray(suppliers) ? suppliers : []).map((s: any) => ({
      label:
        s?.company_name ||
        s?.companyName ||
        s?.supplier_name ||
        s?.supplierName ||
        s?.full_name ||
        s?.fullName ||
        s?.name ||
        "Supplier",
      value: String(s?.id ?? s?._id ?? ""),
      phone:
        s?.supplier_phone ||
        s?.company_phone ||
        s?.phone ||
        s?.mobile ||
        "",
      orgUserId:
        s?.orgUserId ??
        s?.org_user_id ??
        s?.org_userId ??
        s?.org_userID ??
        s?.user?.id ??
        s?.userId ??
        s?.id ??
        s?._id ??
        null,
    }));
  }, [suppliers]);

  const purchaseStockFields = React.useMemo((): Field[] => [
    {
      name: "businessId",
      label: "Business",
      type: "dropdown",
      required: true,
      options: businessList.map((b) => ({ label: b.name, value: b.id })),
    },
    {
      name: "accountId",
      label: "Account",
      type: "dropdown",
      required: true,
      options: accounts.map((a) => ({
        label: `${a.code} – ${a.name}`,
        value: a.id,
      })),
    },
    {
      name: "title",
      label: "Purchases",
      colSpan: 2,
      customRender: (_value: any, _onChange, helpers) => (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2,
            alignItems: "center",
          }}
        >
          <Box>
            <CustomLabel text="Title" fontWeight={500} fontSize={14} />
            <CustomTextField fullWidth value="Purchases" disabled />
          </Box>
          <Box>
            <CustomLabel text="Invoice Number" fontWeight={500} fontSize={14} />
            <CustomTextField
              fullWidth
              value={helpers?.values?.invoiceNumber || ""}
              onChange={(e: any) =>
                helpers?.setFieldValue("invoiceNumber", e.target.value)
              }
            />
          </Box>
        </Box>
      ),
    },
    {
      name: "supplierOrgUserId",
      label: "Supplier",
      type: "dropdown",
      required: true,
      options: [
        { label: "Select supplier", value: "" },
        ...supplierOptions,
      ],
      colSpan: 2,
      customRender: (value, _onChange, helpers) => (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2,
            alignItems: "center",
          }}
        >
          <Box>
            <CustomLabel text="Supplier" fontWeight={500} fontSize={14} />
            <CustomSelect
              label="Supplier"
              value={value || ""}
              options={[
                { label: "Select supplier", value: "" },
                ...(loadingSuppliers
                  ? [{ label: "Loading...", value: "" }]
                  : supplierOptions),
              ]}
              onChange={(v: any) => {
                helpers?.setFieldValue("supplierOrgUserId", v);
                const selected = supplierOptions.find((o) => o.value === v);
                helpers?.setFieldValue("supplierName", selected?.label || "");
                helpers?.setFieldValue("supplierPhone", selected?.phone || "");
              }}
              fullWidth
            />
          </Box>
          <Box>
            <CustomLabel text="Supplier Phone" fontWeight={500} fontSize={14} />
            <CustomTextField
              fullWidth
              value={helpers?.values?.supplierPhone || ""}
              disabled
            />
          </Box>
        </Box>
      ),
    },
    {
      name: "purchaseNotes",
      label: "Notes",
      colSpan: 2,
      customRender: (_value: any, _onChange, helpers) => (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2,
            alignItems: "center",
          }}
        >
          <Box>
            <CustomLabel text="Notes" fontWeight={500} fontSize={14} />
            <CustomTextField
              fullWidth
              value={helpers?.values?.purchaseNotes || ""}
              onChange={(e: any) =>
                helpers?.setFieldValue("purchaseNotes", e.target.value)
              }
            />
          </Box>
          <Box>
            <CustomLabel text="Description" fontWeight={500} fontSize={14} />
            <CustomTextField
              fullWidth
              value={helpers?.values?.purchaseDescription || ""}
              onChange={(e: any) =>
                helpers?.setFieldValue("purchaseDescription", e.target.value)
              }
            />
          </Box>
        </Box>
      ),
    },
    {
      name: "lines",
      label: "Items",
      colSpan: 2,
      customRender: (_value: any, onChange: (v: any) => void, helpers) => (
        <PurchaseItemsEditor
          value={_value}
          onChange={onChange}
          vatValue={helpers?.values?.vat || 0}
          onVatChange={(v) => helpers?.setFieldValue("vat", v)}
          discountValue={helpers?.values?.discount || 0}
          onDiscountChange={(v) => helpers?.setFieldValue("discount", v)}
          promoDiscountValue={helpers?.values?.promoDiscount || 0}
          onPromoDiscountChange={(v) => helpers?.setFieldValue("promoDiscount", v)}
          promoValue={helpers?.values?.promoValue || 0}
          onPromoValueChange={(v) => helpers?.setFieldValue("promoValue", v)}
          servicesList={(Array.isArray(productsList) ? productsList : []).map((p: any) => ({
            id: p?.id ?? p?._id,
            name: p?.name ?? p?.title ?? "Product",
            unit_cost: p?.cost_price ?? p?.costPrice ?? 0,
            price: p?.cost_price ?? p?.costPrice ?? 0,
            type: p?.package_type ?? p?.packageType ?? "",
          }))}
          isEditMode
        />
      ),
    },
  ], [businessList, productsList, accounts, supplierOptions, loadingSuppliers]); 

  const transferStockFields = React.useMemo((): Field[] => [
    {
      name: "fromBusiness",
      label: "From Business",
      type: "dropdown",
      options: businessList.map((b) => ({ label: b.name, value: b.id })),
    },
    {
      name: "toBusiness",
      label: "To Business",
      type: "dropdown",
      options: businessList.map((b) => ({ label: b.name, value: b.id })),
    },
    {
      name: "lines",
      label: "Items",
      colSpan: 2,
      customRender: (_value: any, onChange: (v: any) => void, helpers) => (
        <PurchaseItemsEditor
          value={_value}
          onChange={onChange}
          vatValue={helpers?.values?.vat || 0}
          onVatChange={(v) => helpers?.setFieldValue("vat", v)}
          discountValue={helpers?.values?.discount || 0}
          onDiscountChange={(v) => helpers?.setFieldValue("discount", v)}
          promoDiscountValue={helpers?.values?.promoDiscount || 0}
          onPromoDiscountChange={(v) => helpers?.setFieldValue("promoDiscount", v)}
          promoValue={helpers?.values?.promoValue || 0}
          onPromoValueChange={(v) => helpers?.setFieldValue("promoValue", v)}
          servicesList={(Array.isArray(productsList) ? productsList : []).map((p: any) => ({
            id: p?.id ?? p?._id,
            name: p?.name ?? p?.title ?? "Product",
            unit_cost: p?.cost_price ?? p?.costPrice ?? 0,
            price: p?.cost_price ?? p?.costPrice ?? 0,
            type: p?.package_type ?? p?.packageType ?? "",
          }))}
          isEditMode
        />
      ),
    },
  ], [businessList, productsList]);

  const adjustStockFields = React.useMemo((): Field[] => [
    {
      name: "businessId",
      label: "Business",
      type: "dropdown",
      required: true,
      options: businessList.map((b) => ({ label: b.name, value: b.id })),
    },
    {
      name: "productId",
      label: "Product",
      type: "dropdown",
      required: true,
      options: productsList.map((p) => ({ label: p.name, value: p.id })),
    },
    { name: "qty", label: "Quantity", type: "number", required: true },
  ], [businessList, productsList]);

  return (
    <Box sx={{ padding: 2 }}>

      <Box
        sx={{
          display: "grid",
          gap: { xs: 2, sm: 3 },
          gridTemplateColumns: {
            xs: "repeat(1, 1fr)",
            sm: "repeat(2, 1fr)",
            md: "repeat(3, 1fr)",
          },
          alignItems: "stretch",
          justifyContent: "center",
          mt: 2,
        }}
      >
        {summaryCards.map((card) => {
          const Icon = card.icon;
          const valueText = loading
            ? "Loading..."
            : summaryError
            ? "Error"
            : formatValue(card.value);

          return (
            <Card key={card.label} sx={summaryCardSx}>
              <CardContent sx={summaryCardContentSx}>
                <Icon
                  sx={{
                    fontSize: { xs: 32, sm: 30 },
                    color: summaryCardPrimaryTextColor,
                  }}
                />
                <CustomLabel
                  text={card.label}
                  fontWeight={600}
                  sx={{
                    fontSize: { xs: "0.95rem", sm: "0.85rem" },
                    color: summaryCardPrimaryTextColor,
                  }}
                />
                <CustomLabel
                  text={valueText}
                  fontWeight={700}
                  center
                  sx={{
                    fontSize: { xs: "1.6rem", sm: "1.4rem" },
                    color: summaryCardPrimaryTextColor,
                  }}
                />
                <CustomLabel
                  text={summaryError ? summaryError : summaryScopeLabel}
                  fontWeight={500}
                  sx={{
                    fontSize: { xs: "0.75rem", sm: "0.7rem" },
                    color: summaryCardSecondaryTextColor,
                  }}
                />
              </CardContent>
            </Card>
          );
        })}
      </Box>

      <Divider sx={{ my: { xs: 2, sm: 3 }, borderColor: COLORS.BorderColor }} />

      <SearchHeaderContainer
        search={search}
        setSearch={setSearch}
        buttonConfig={[
          {
            size: "small",
            bgColor: COLORS.MainColor,
            startIcon: <SwapHorizIcon />,
            onClick: () => {
              setOpenTransferDialog(true);
            },
            textColor: COLORS.WhiteText,
          },
          {
            size: "small",
            bgColor: COLORS.MainColor,
            startIcon: <AddIcon />,
            onClick: () => {
              setStockDialogTab(0);
              setOpenAddStockDialog(true);
            },
            textColor: COLORS.WhiteText,
          },
        ]}
        
      />
      
      <CustomTable<StockRow>
        filters={{ page: 0, size: 10, search }}
        clientSidePagination
        loading={tableLoading}
        fields={[
          { key: "productName", header: "Product Name" },
          { key: "productType", header: "Type" },
          { key: "qty", header: "Qty" },
          {
            key: "costPrice",
            header: "Cost/Unit",
            transform: (r) =>
              r.costPrice !== null && r.costPrice !== undefined
                ? Number(r.costPrice).toFixed(2)
                : "-",
          },
          {
            key: "salePrice",
            header: "Retail/Unit",
            transform: (r) =>
              r.salePrice !== null && r.salePrice !== undefined
                ? Number(r.salePrice).toFixed(2)
                : "-",
          },
          {
            key: "totalCost",
            header: "Total Cost",
            transform: (r) =>
              r.totalCost !== null && r.totalCost !== undefined
                ? Number(r.totalCost).toFixed(2)
                : "-",
          },
          {
            key: "totalRetail",
            header: "Total Retail",
            transform: (r) =>
              r.totalRetail !== null && r.totalRetail !== undefined
                ? Number(r.totalRetail).toFixed(2)
                : "-",
          },
          {
            key: "profit",
            header: "Profit",
            transform: (r) =>
              r.profit !== null && r.profit !== undefined
                ? Number(r.profit).toFixed(2)
                : "-",
          },
          { key: "businessName", header: "Business" },
          {
            key: "updatedAt",
            header: "Last Updated",
            transform: (r) =>
              r.updatedAt
                ? (() => {
                    const d = new Date(r.updatedAt);
                    if (Number.isNaN(d.getTime())) return "-";
                    return `${d.getDate()}-${d.getMonth() + 1}-${d.getFullYear()}`;
                  })()
                : "-",
          },
        ]}
        actions={wiredActions}
        fetchFunc={fetchStockFunc}
      />
      <GenericFormDialog 
        open={adjustOpen}
        title="Adjust Stock"
        onClose={() => setAdjustOpen(false)}
        fields={adjustStockFields}
        initialValues={adjustInitialValues}
        disableSubmitUntilValid
        onSubmit={async (values) => {
          try {
            const productId = values.productId;
            const bizId = values.businessId;
            const nextQty = Number(values.qty);
            if (!productId || Number.isNaN(nextQty)) {
              toast.error("Please select product and quantity.");
              return;
            }
            const matched = rows.find(
              (r: any) =>
                String(r.productId || r.id) === String(productId) &&
                String(r.businessId || businessId || "") === String(bizId || "")
            );
            const baseQty = Number(matched?.qty ?? adjustInitialValues.qty ?? 0);
            const qtyDelta = nextQty - baseQty;

            await adjustStock({
              productId,
              businessId: bizId,
              qtyDelta,
            });

            const res = await fetchStock(businessId);
            const list = pickStockList(res);
            setRows(list.map(mapStockRecord));

            const summary = await fetchStockSummary(businessId);
            setStockSummary(summary);

            toast.success("Stock adjusted successfully!");
            setAdjustOpen(false);
          } catch (error: any) {
            console.error("Adjust stock failed:", error);
            toast.error(
              error?.response?.data?.message ||
                error?.response?.data?.error ||
                error?.message ||
                "Failed to adjust stock."
            );
          }
        }}
      />
      <GenericFormDialog
        open={openAddStockDialog}
        title="Add Stock"
        onClose={() => setOpenAddStockDialog(false)}
        tabs={[
          { label: "Create Stock", fields: createStockFields },
          { label: "Purchases", fields: purchaseStockFields },
        ]}
        initialTab={stockDialogTab}
        onTabChange={setStockDialogTab}
        initialValues={{
          businessId: businessId,
          lines: [],
          accountId: defaultExpenseAccountId,
          invoiceNumber: "",
        }}
        disableSubmitUntilValid
        onSubmit={async (values) => {
          try {
            const isPurchases = stockDialogTab === 1;
            if (isPurchases) {
              const bizId = values.businessId;
              const accountId = values.accountId;
              const lines = Array.isArray(values.lines) ? values.lines : [];
              const clampPercent = (value: number) =>
                Math.min(100, Math.max(0, value));

              const subtotal = lines.reduce(
                (sum: number, line: any) => sum + (Number(line?.totalPrice) || 0),
                0
              );
              const discountPercent = clampPercent(Number(values.discount) || 0);
              const vatPercent = clampPercent(Number(values.vat) || 0);
              const discountAmount = (subtotal * discountPercent) / 100;
              const amountAfterDiscount = subtotal - discountAmount;
              const vatAmount = (amountAfterDiscount * vatPercent) / 100;
              const netAmount =
                Math.round((amountAfterDiscount + vatAmount) * 100) / 100;

              const purchaseItems = lines.map((line: any) => ({
                description:
                  line.serviceLabel || String(line.service || "Item"),
                amount: Number(line.totalPrice) || 0,
              }));

              if (!bizId) {
                toast.error("Please select a business.");
                return;
              }
              if (!accountId) {
                toast.error("Please select an account.");
                return;
              }
              if (purchaseItems.length === 0) {
                toast.error("Please add at least one item.");
                return;
              }

              await createExpense({
                amount: netAmount,
                currency: "USD",
                title: "Purchases",
                notes: values.purchaseNotes || "",
                accountId: Number(accountId),
                description:
                  values.purchaseDescription ||
                  (values.supplierName
                    ? `Purchases from ${values.supplierName}`
                    : "Purchases"),
                items: purchaseItems,
                metadata: {
                  type: "PURCHASE",
                  supplierName: values.supplierName || null,
                  supplierPhone: values.supplierPhone || null,
                  supplierOrgUserId: values.supplierOrgUserId || null,
                  invoice_nb: values.invoiceNumber || values.invoice_nb || null,
                  lines,
                  discountPercent,
                  vatPercent,
                  businessId: bizId,
                },
              });
              toast.success("Purchase expense created successfully!");
            } else {
              if (!values.productId || !values.qty) {
                toast.error("Please select product and quantity.");
                return;
              }
              await createStock({
                productId: values.productId,
                businessId: values.businessId,
                qty: Number(values.qty),
              });
              toast.success("Stock added successfully!");
            }

            const res = await fetchStock(businessId);
            const list = pickStockList(res);
            setRows(list.map(mapStockRecord));

            const summary = await fetchStockSummary(businessId);
            setStockSummary(summary);

            setOpenAddStockDialog(false);
          } catch (error: any) {
            console.error("Add stock failed:", error);
            toast.error(
              error?.response?.data?.message ||
                error?.response?.data?.error ||
                error?.message ||
                "Failed to add stock."
            );
          }
        }}
      />

      <GenericFormDialog
        open={openTransferDialog}
        title="Transfer Stock"
        onClose={() => setOpenTransferDialog(false)}
        fields={transferStockFields}
        initialValues={{
          fromBusiness: businessId,
          lines: [],
        }}
        disableSubmitUntilValid
        onSubmit={async (values) => {
          try {
            const fromBusinessId = values.fromBusiness;
            const toBusinessId = values.toBusiness;
            if (!fromBusinessId || !toBusinessId) {
              toast.error("Please select both businesses.");
              return;
            }
            const lines = Array.isArray(values.lines) ? values.lines : [];
            const payloads = lines
              .map((line: any) => ({
                productId: String(line?.serviceId ?? line?.service ?? ""),
                qty: Number(line?.quantity) || 0,
              }))
              .filter((p: any) => p.productId && p.qty > 0);

            if (payloads.length === 0) {
              toast.error("Please add at least one item with quantity.");
              return;
            }

            await transferStock(
              payloads.map((p) => ({
                productId: p.productId,
                fromBusinessId,
                toBusinessId,
                qty: p.qty,
              }))
            );

            const res = await fetchStock(businessId);
            const list = pickStockList(res);
            setRows(list.map(mapStockRecord));

            const summary = await fetchStockSummary(businessId);
            setStockSummary(summary);

            toast.success("Stock transferred successfully!");
            setOpenTransferDialog(false);
          } catch (error: any) {
            console.error("Transfer stock failed:", error);
            toast.error(
              error?.response?.data?.message ||
                error?.response?.data?.error ||
                error?.message ||
                "Failed to transfer stock."
            );
          }
        }}
      />

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => {
          setConfirmOpen(false);
          setToDeleteRow(null);
        }}
        title="Delete entry"
        message="Are you sure you want to delete this entry? This action cannot be undone."
        onConfirm={async () => {
          if (!toDeleteRow) return;
          try {
            await deleteStock(
              String(toDeleteRow.productId || toDeleteRow.id),
              toDeleteRow.businessId || businessId
            );
            setRows((prev) =>
              prev.filter(
                (r) => String(r.id) !== String(toDeleteRow.id)
              )
            );
            const summary = await fetchStockSummary(businessId);
            setStockSummary(summary);
            toast.success("Stock deleted successfully!");
          } catch (error: any) {
            console.error("Failed to delete stock", error);
            toast.error(
              error?.response?.data?.message ||
                error?.response?.data?.error ||
                error?.message ||
                "Failed to delete stock."
            );
          } finally {
            setConfirmOpen(false);
            setToDeleteRow(null);
          }
        }}
      />
      
    </Box>
  );
};

export default Stocks;
